import type { BalanceData, ChestResult } from "@highmarch/domain";
import type { WyrmGrant } from "@highmarch/app";
import { SKIN_LOOKS } from "../render/skins/looks";

/**
 * The Starfall Wyrm's moment as plain data (M40): when each beat of the arrival lands, what the choice sheet says and
 * allows, and what the unlock reveal shows. No DOM, no three.js: level/wyrmMoment.ts and ui/wyrm*.ts stage it, the
 * tests pin it (test/wyrmModel.test.ts).
 */

/** Captain Bram's lines, by beat (docs/VOICE.md: first person, brief, gruff and warm). */
export const WYRM_LINES = {
  spawn: "Stars falling, Commander! That's the Starfall Wyrm. Bring it down and its treasure is ours!",
  returns: "It's back, and still bleeding. Finish it!",
  slainSkin: "We did it! Starfall treasure, and it's yours.",
  slainChest: "Every Starfall prize is already ours. Take the hoard instead!",
  escape: "It's slipping away into the stars!",
  release: "Fly well, old wyrm. The stars will send you back one day.",
} as const;

/**
 * Every other line the Wyrm's moment shows, in the game's voice (docs/VOICE.md, the M39 copy pass; Kevin 2026-10-13:
 * engaging gameplay text, never odds, percentages or mechanics). The titles slam in as written.
 */
export const WYRM_COPY = {
  arrivalTitle: (name: string) => `THE ${name.toUpperCase()} DESCENDS!`,
  arrivalSub: "Slay it for a <b>Starfall skin</b> · if it escapes, no harm done",
  returnTitle: "IT RETURNS!",
  returnSub: (pct: number) => (pct < 100 ? `Wounded · ${pct}% health left · finish it!` : "Back at full strength · bring it down!"),
  escapeTitle: "THE WYRM ESCAPES!",
  escapeSub: "It soars off into the night · no harm done",
  slainTitle: "WYRM SLAIN!",
  slainSub: "Its treasure is yours",
  choiceTitle: (name: string) => `THE ${name.toUpperCase()} ESCAPED`,
  choiceLead: (wounded: boolean) => (wounded ? "It's wounded and fleeing. Call it back to finish the fight, or let it fly." : "It's fleeing into the stars. Call it back for another fight, or let it fly."),
  retry: "CALL IT BACK",
  release: "Let it go",
  treasury: (gems: string) => `You have ${gems} gems`,
  /** Short of the price: the shortfall and that it will fly again (docs/VOICE.md: never a push to buy). */
  short: (gems: string) => `${gems} gems short · it will fly again one day`,
  /** The quiet top-up link under the two answers (Kevin 2026-10-13: "add a way to open the shop for top ups"). */
  topUp: "Top up gems",
  revealTitle: "NEW SKIN UNLOCKED",
  revealSub: "Won from the Starfall Wyrm",
  equip: "EQUIP",
  later: "Later",
  wornTower: "On your tower now. Looks only — pure glory",
  wornFortress: "Your fortress wears it from the next battle",
  equipLater: "Wear it any time from Skins",
  hoardTitle: "STARFALL HOARD",
  hoardSub: "Every Starfall skin is already yours. Take its hoard instead",
  cardLine: "✦ LEGENDARY · Slay it for a Starfall skin. If it escapes, no harm done",
  markerLabel: "Show the Starfall Wyrm",
} as const;

/** Its health on the choice sheet: the number stays (docs/VOICE.md "Numbers stay"). */
export function woundWords(frac: number): string {
  return frac >= 0.999 ? "Full health" : `Wounded · ${Math.round(frac * 100)}% health left`;
}

/** The arrival's beats, in milliseconds from WyrmSpawned (the `spawn` clip runs 2.53 s: art/unitkit/README.md). */
export const WYRM_ARRIVAL = {
  /** The camera leans toward the portal and the ground starts to tremble. */
  ShakeMs: 250,
  /** The clip's great downbeat (1.0 s): the flash, the shockwave, the burst — and the title slams in. */
  SlamMs: 1000,
  /** The roar (the clip rears and roars 1.3–1.8 s): the deep stinger and a second kick. */
  RoarMs: 1300,
  /** Bram's line slides in. */
  BramMs: 1700,
  /** The view goes back to where the player had it. */
  CamBackMs: 3800,
  /** The banner leaves. */
  LeaveMs: 5600,
  /** A retry's shorter return ("IT RETURNS!"): its slam and its banner's hold. */
  ReturnSlamMs: 1000,
  ReturnLeaveMs: 2800,
} as const;

export type ArrivalBeat = "night" | "shake" | "slam" | "roar" | "bram" | "camBack" | "leave";

/**
 * The arrival's beats in order (`retry`: the return's shorter cut — no Bram, no camera push). Reduced effects keep
 * every beat that carries information (the night, the slam's banner, Bram) and drop the shake and the camera's moves.
 */
export function arrivalPlan(retry: boolean, reduced: boolean): { readonly at: number; readonly beat: ArrivalBeat }[] {
  const A = WYRM_ARRIVAL;
  const all: { at: number; beat: ArrivalBeat }[] = retry
    ? [{ at: 0, beat: "night" }, { at: A.ShakeMs, beat: "shake" }, { at: A.ReturnSlamMs, beat: "slam" }, { at: A.RoarMs, beat: "roar" }, { at: A.ReturnLeaveMs, beat: "leave" }]
    : [{ at: 0, beat: "night" }, { at: A.ShakeMs, beat: "shake" }, { at: A.SlamMs, beat: "slam" }, { at: A.RoarMs, beat: "roar" }, { at: A.BramMs, beat: "bram" }, { at: A.CamBackMs, beat: "camBack" }, { at: A.LeaveMs, beat: "leave" }];
  return reduced ? all.filter((b) => b.beat !== "shake" && b.beat !== "camBack") : all;
}

/** The escape's beats: the flee clip soars off (1.83 s), the ribbon, then the choice sheet. */
export const WYRM_ESCAPE = { RibbonMs: 300, ChoiceMs: 2300, RibbonHoldMs: 2000 } as const;

/** Health thresholds (fractions) at which it roars as it is hurt. */
export const WYRM_ROAR_AT: readonly number[] = [0.75, 0.5, 0.25];

/** The next roar threshold crossed going from `before` to `after` (health fractions), or −1. */
export function roarCrossed(before: number, after: number): number {
  for (const t of WYRM_ROAR_AT) if (before > t && after <= t) return t;
  return -1;
}

// -----------------------------------------------------------------------------------------------------------------
// The choice sheet
// -----------------------------------------------------------------------------------------------------------------

/** "2,500" (grouping commas, the gem price as the shop writes it). */
export const fmtGems = (n: number): string => Math.max(0, Math.floor(n)).toLocaleString("en-GB");

export interface WyrmChoiceModel {
  readonly title: string;
  readonly lead: string;
  /** The HP it would come back with, as a fraction and in words ("Wounded: 38 % health left"). */
  readonly hpFrac: number;
  readonly hpText: string;
  readonly retryLabel: string;
  readonly cost: string;
  readonly affordable: boolean;
  /** Under the retry button: the treasury, or the shortfall. */
  readonly balanceText: string;
  readonly releaseLabel: string;
  /** Short of gems with a store to buy from: the TOP UP button (the gem packs over the paused battle, wyrmTopUp.ts). */
  readonly topUp: string | null;
  /** Gems missing for CHALLENGE AGAIN (0 when affordable): the packs that cover it are suggested. */
  readonly shortfall: number;
}

/**
 * What the sheet says (docs/VOICE.md; no odds or percentages: its wound in words, the price on its button). `gems`: the
 * Commander's balance; `cost`: wyrmRetryGems; `hpLeft`/`hpMax`: wyrmHpLeft; `store`: gem packs can be bought here (the
 * one in-battle top-up, domain `gemPacksAllowed("wyrmChoice")`) — short of gems, the sheet then offers TOP UP.
 */
export function wyrmChoiceModel(o: { gems: number; cost: number; hpLeft: number; hpMax: number; name: string; store?: boolean }): WyrmChoiceModel {
  const frac = Math.max(0, Math.min(1, o.hpLeft / Math.max(1, o.hpMax)));
  const affordable = o.gems >= o.cost, C = WYRM_COPY;
  return {
    title: C.choiceTitle(o.name),
    lead: C.choiceLead(frac < 0.999),
    hpFrac: frac,
    hpText: woundWords(frac),
    retryLabel: C.retry,
    cost: fmtGems(o.cost),
    affordable,
    balanceText: affordable ? C.treasury(fmtGems(o.gems)) : C.short(fmtGems(o.cost - o.gems)),
    releaseLabel: C.release,
    topUp: !affordable && o.store ? C.topUp : null,
    shortfall: Math.max(0, o.cost - o.gems),
  };
}

// -----------------------------------------------------------------------------------------------------------------
// The top-up (Kevin 2026-10-13: "add a way to open the shop for top ups")
// -----------------------------------------------------------------------------------------------------------------

export const WYRM_TOPUP = {
  title: "TOP UP GEMS",
  lead: "No rush: the Wyrm waits while you choose",
  suggested: "Covers it",
  back: "Back",
  thanks: (gems: string) => `+${gems} gems`,
  pending: "Awaiting approval: it arrives the moment it's given",
  sandbox: "Test build: nothing is charged",
} as const;

/** How many packs the top-up lays out: the smallest that covers the shortfall and the next ones up. */
export const TOPUP_SHOWN = 3;

/**
 * The gem packs the top-up offers (Kevin: "suggesting the pack(s) that cover the shortfall"): the smallest packs that
 * each cover it alone, smallest first, at most `TOPUP_SHOWN`, the first suggested; when none does, the biggest. `index`
 * is the pack's place in the catalogue (its heap art, ui/deals/dealsTiles.ts gemPackArt).
 */
export function topUpPlan<P extends { readonly gems: number }>(packs: readonly P[], shortfall: number): { pack: P; index: number; suggested: boolean }[] {
  const all = packs.map((pack, index) => ({ pack, index })).sort((a, b) => a.pack.gems - b.pack.gems);
  const cover = all.filter((p) => p.pack.gems >= shortfall).slice(0, TOPUP_SHOWN);
  const shown = cover.length > 0 ? cover : all.slice(-1);
  return shown.map((p, i) => ({ ...p, suggested: i === 0 }));
}

// -----------------------------------------------------------------------------------------------------------------
// The unlock reveal
// -----------------------------------------------------------------------------------------------------------------

/** Where a Starfall skin is worn: one tower type's, or the fortress's. */
export type SkinTarget = { readonly kind: "tower"; readonly towerId: string } | { readonly kind: "fortress" };

/** `tower.starfall_archer` → the archer; `fortress.starfall` → the fortress. */
export function skinTarget(skinId: string): SkinTarget {
  if (skinId.startsWith("fortress.")) return { kind: "fortress" };
  const m = /^tower\.starfall_(.+)$/.exec(skinId);
  return { kind: "tower", towerId: m ? m[1] : skinId.replace(/^tower\./, "") };
}

/** "Starfall Archer Tower", "Starfall Arcane Spire", "Starfall Barracks", "Starfall Citadel". */
export function skinName(B: BalanceData, skinId: string): string {
  const t = skinTarget(skinId);
  if (t.kind === "fortress") return SKIN_LOOKS[skinId]?.name ?? "Starfall Citadel";
  const name = B.towers.find((x) => x.id === t.towerId)?.name ?? t.towerId;
  return `Starfall ${name}${/\s|Barracks$/.test(name) ? "" : " Tower"}`;
}

/** The skin's render (art/interface/skins/<id with _>@2x.png), as a path under interface/. */
export const skinArtPath = (skinId: string): string => `skins/${skinId.replace(".", "_")}@2x.png`;

export type WyrmReveal =
  | {
    readonly kind: "skin"; readonly skinId: string; readonly title: string; readonly name: string; readonly art: string; readonly target: SkinTarget;
    /** Worn at once on the live board (a tower skin re-dresses where it stands), or from the next battle (the fortress's model). */
    readonly live: boolean; readonly equipLabel: string; readonly wornNote: string; readonly line: string;
  }
  | { readonly kind: "chest"; readonly title: string; readonly rarity: number; readonly gems: string; readonly sub: string; readonly line: string };

/** What the reveal shows for a grant (app grantWyrmKill), or null for none. */
export function revealPlan(B: BalanceData, grant: WyrmGrant | null): WyrmReveal | null {
  if (!grant) return null;
  if (grant.kind === "chest") return chestReveal(grant.chest, grant.gems);
  const target = skinTarget(grant.skinId), live = target.kind === "tower";
  return {
    kind: "skin", skinId: grant.skinId, title: WYRM_COPY.revealTitle, name: skinName(B, grant.skinId), art: skinArtPath(grant.skinId), target, live,
    equipLabel: WYRM_COPY.equip,
    wornNote: live ? WYRM_COPY.wornTower : WYRM_COPY.wornFortress,
    line: WYRM_LINES.slainSkin,
  };
}

function chestReveal(chest: ChestResult, gems: number): WyrmReveal {
  return { kind: "chest", title: WYRM_COPY.hoardTitle, rarity: chest.rarity, gems: fmtGems(gems), sub: WYRM_COPY.hoardSub, line: WYRM_LINES.slainChest };
}

/** How long the reveal holds before it flies to the HUD (an EQUIP or a tap goes sooner), ms. */
export const WYRM_REVEAL = { DelayMs: 1500, HoldMs: 5200, FlyMs: 700 } as const;
